import React from 'react';
import { Switch } from 'react-router-dom';

import { RolesWithProduct } from '@customTypes/api/Commons';
import { FEATURE_FLAGS } from '@src/constants';
import featureFlags from '@src/constants/featureFlags';
import { useAuthorizationContext } from '@src/context/AuthorizationContext';
import { lazyLoad } from '@src/utils/loadable';
import { isDateInSetTargetPeriod } from '@src/utils/utils';

import { PrivateLayout } from '../layouts';

import pathnames from './pathnames';
import Route from './RouteWithRedirect';

const AgencyNewsUpdates = lazyLoad(
  () => import('@pages/AgencyNews/Updates'),
  module => module.AgencyNewsUpdates,
);
const Maintain = lazyLoad(
  () => import('@pages/PlanActivity/Maintain'),
  module => module.Maintain,
);
const AreSheduleDetail = lazyLoad(
  () => import('@pages/PlanActivity/Maintain/ARESchedule/ScheduleDetail'),
  module => module.ScheduleDetail,
);

const Recruit = lazyLoad(
  () => import('@pages/PlanActivity/Recruit'),
  module => module.Recruit,
);

const SPVDashboard = lazyLoad(
  () => import('@pages/PerformanceDashboard/SPVDashboard'),
  module => module.SPVDashboardPage,
);
// Note: Replaced by temporary-screen for now
// const BMDashboard = lazyLoad(
//   () => import('@pages/PerformanceDashboard/BMDashboard'),
//   module => module.BMDashboardPage,
// );
// const ABPMDashboard = lazyLoad(
//   () => import('@pages/PerformanceDashboard/ABPMDashboard'),
//   module => module.ABPMDashboardPage,
// );
const TemporaryScreen = lazyLoad(
  () => import('@pages/TemporaryScreen'),
  module => module.TemporaryScreen,
);

const GoalSettings = lazyLoad(
  () => import('@pages/PerformanceDashboard/GoalSettings'),
  module => module.GoalSettingsPage,
);

const AgentPerformanceThisMonth = lazyLoad(
  () => import('@pages/PerformanceDashboard/AgentPerformance/ThisMonth'),
  module => module.AgentPerformanceThisMonthPage,
);

const AgentPerformanceHistorical = lazyLoad(
  () => import('@pages/PerformanceDashboard/AgentPerformance/Historical'),
  module => module.AgentPerformanceHistoricalPage,
);

const AREPerformance = lazyLoad(
  () => import('@pages/PerformanceDashboard/AREPerformance'),
  module => module.AREPerformancePage,
);

const KpiIncentive = lazyLoad(
  () => import('@pages/PerformanceDashboard/KpiIncentive'),
  module => module.KpiIncentive,
);

const NotFoundPage = lazyLoad(
  () => import('@components/NotFoundPage'),
  module => module.NotFoundPage,
);

const AgentManagement = lazyLoad(
  () => import('@pages/TeamManagement/AgentManagement'),
  module => module.AgentManagement,
);
const AgentManagementDetail = lazyLoad(
  () => import('@pages/TeamManagement/AgentManagement/Detail'),
  module => module.AgentManagementDetail,
);

const Hierarchy = lazyLoad(
  () => import('@pages/TeamManagement/Hierarchy'),
  module => module.Hierarchy,
);

const AddNewAssignment = lazyLoad(
  () => import('@pages/PlanActivity/Maintain/AddNewAssignment'),
  module => module.AddNewAssignment,
);

const AddNewRecruitAssignment = lazyLoad(
  () => import('@pages/PlanActivity/Recruit/AddNewAssignment'),
  module => module.AddNewRecruitAssignment,
);

const WeeklyMeeting = lazyLoad(
  () => import('@pages/PlanActivity/WeeklyMeeting'),
  module => module.WeeklyMeeting,
);
const AddNewWeeklyMeetingReport = lazyLoad(
  () => import('@pages/PlanActivity/WeeklyMeeting/AddNewReport'),
  module => module.AddNewWeeklyMeetingReport,
);

const JoinVisit = lazyLoad(
  () => import('@pages/PlanActivity/JoinVisit'),
  module => module.JoinVisit,
);

const OneOnOne = lazyLoad(
  () => import('@pages/PlanActivity/OneOnOne'),
  module => module.OneOnOne,
);
const AddNewOneOnOneReport = lazyLoad(
  () => import('@pages/PlanActivity/OneOnOne/AddNewReport'),
  module => module.AddNewReport,
);

const AddNewJoinVisitReport = lazyLoad(
  () => import('@pages/PlanActivity/JoinVisit/AddNewReport'),
  module => module.AddNewReport,
);

const MaintainReport = lazyLoad(
  () => import('@pages/Reports/MaintainReport'),
  module => module.MaintainReport,
);

const MaintainReportList = lazyLoad(
  () => import('@pages/Reports/MaintainReport/List'),
  module => module.MaintainReportList,
);

const RecruitReport = lazyLoad(
  () => import('@pages/Reports/Recruit'),
  module => module.RecruitReport,
);

const LeadsTracker = lazyLoad(
  () => import('@pages/LeadsTracker/List'),
  module => module.LeadsTrackerList,
);
const LeadsTrackerDetail = lazyLoad(
  () => import('@pages/LeadsTracker/Detail'),
  module => module.LeadsTrackerDetail,
);

const UserGuide = lazyLoad(
  () => import('@pages/UserGuide'),
  module => module.UserGuide,
);

const UnauthorizedPage = lazyLoad(
  () => import('@pages/Unauthorized'),
  module => module.UnauthorizedPage,
);

const Debug = lazyLoad(
  () => import('@pages/Debug'),
  module => module.Debug,
);

export const AppRouter = () => {
  const { isAllowed, rolesProduct } = useAuthorizationContext();

  const isPBF = rolesProduct === RolesWithProduct['SPV.PBF'];

  const renderDynamicHome = () => {
    const homeRoutes: { [key: string]: React.ReactNode } = {
      [RolesWithProduct['SPV.4W']]: (
        <Route
          key="SpvDashboard"
          exact
          path="/"
          enabled={isAllowed('spv-dashboard')}
          render={() => <SPVDashboard />}
        />
      ),
      [RolesWithProduct['SPV.2W']]: (
        <Route
          key="SpvDashboard"
          exact
          path="/"
          enabled={isAllowed('spv-dashboard')}
          render={() => <SPVDashboard />}
        />
        // [String(Roles.Abpm)]: (
        //   <Route
        //     key="AbpmDashboard"
        //     exact
        //     path="/"
        //     enabled={isAllowed('area-dashboard')}
        //     render={() => <ABPMDashboard />}
        //   />
        // ),
        // [String(Roles.Bm)]: (
        //   <Route
        //     key="BmDashboard"
        //     exact
        //     path="/"
        //     enabled={isAllowed('branch-dashboard')}
        //     render={() => <BMDashboard />}
        //   />
        // ),
      ),
    };
    return (
      homeRoutes[rolesProduct] ?? (
        <Route
          key="temporary-screen"
          exact
          path="/"
          render={() => <TemporaryScreen />}
        />
      )
    );
  };

  return (
    <PrivateLayout>
      <Switch>
        <Route
          key="debug"
          exact
          path={pathnames.debug()}
          enabled={featureFlags.routes.debug()}
          render={() => <Debug />}
        />
        <Route
          key="agency-news-updates"
          exact
          path={pathnames.agencyNewsUpdates()}
          enabled={isAllowed('agency-news.updates') && !isPBF}
          render={() => <AgencyNewsUpdates />}
        />

        <Route
          key="agent-performance"
          exact
          path={pathnames.agentPerformance()}
          enabled={isAllowed('agent-performance.this-month') && !isPBF}
          render={() => <AgentPerformanceThisMonth />}
        />
        <Route
          key="agent-performance-historical"
          exact
          path={pathnames.historicalAgentPerformance(':agentMarketingId')}
          enabled={isAllowed('agent-performance.historical') && !isPBF}
          render={() => <AgentPerformanceHistorical />}
        />
        <Route
          key="maintain"
          exact
          path={pathnames.maintainActivity()}
          enabled={
            isAllowed('maintain.assignment') ||
            isAllowed('maintain.are-schedule')
          }
          render={() => <Maintain />}
        />
        <Route
          key="are-schedule-detail"
          exact
          path={pathnames.maintainActivityScheduleDetail(':id')}
          render={() => <AreSheduleDetail />}
        />
        <Route
          key="are-performance"
          exact
          path={pathnames.arePerformance()}
          enabled={isAllowed('are-performance') && !isPBF}
          render={() => <AREPerformance />}
        />
        <Route
          key="kpi-incentive"
          exact
          path={pathnames.kpiIncentive()}
          enabled={isAllowed('kpi-incentive') && !isPBF}
          render={() => <KpiIncentive />}
        />
        {renderDynamicHome()}
        <Route
          key="SPVPerformance"
          exact
          path={pathnames.spvPerformance()}
          enabled={isAllowed('spv-performance') && !isPBF}
          render={() => <SPVDashboard />}
        />
        <Route
          key="set-target"
          exact
          enabled={
            isAllowed('goal-settings') &&
            isDateInSetTargetPeriod(new Date(Date.now())) &&
            !isPBF
          }
          path={pathnames.setTarget()}
          render={() => <GoalSettings />}
        />
        <Route
          key="agent-management"
          exact
          path={pathnames.agentManagement()}
          enabled={isAllowed('agent-management') && !isPBF}
          render={() => <AgentManagement />}
        />
        <Route
          key="agent-management-detail"
          exact
          path={pathnames.agentManagementDetail(':baId')}
          enabled={isAllowed('agent-management') && !isPBF}
          render={() => <AgentManagementDetail />}
        />
        <Route
          key="hierarchy"
          exact
          path={pathnames.teamHierarchy()}
          enabled={isAllowed('hierarchy') && !isPBF}
          render={() => <Hierarchy />}
        />
        <Route
          key="recruit"
          exact
          path={pathnames.recruitActivity()}
          enabled={
            (isAllowed('recruitment.recruitment-area') ||
              isAllowed('recruitment.recruitment-list')) &&
            !isPBF
          }
          render={() => <Recruit />}
        />
        <Route
          key="add-new-assignment"
          exact
          path={pathnames.addNewMaintainAssignment()}
          enabled={isAllowed('maintain.assignment', 'create')}
          render={() => <AddNewAssignment />}
        />
        <Route
          key="add-new-recruit-assignment"
          exact
          path={pathnames.addNewRecruitAssignment()}
          enabled={
            isAllowed('recruitment.recruitment-area.assignment', 'create') &&
            !isPBF
          }
          render={() => <AddNewRecruitAssignment />}
        />
        <Route
          key="weekly-meeting"
          exact
          path={pathnames.weeklyMeeting()}
          render={() => <WeeklyMeeting />}
          enabled={
            FEATURE_FLAGS.routes.weeklyMeeting() &&
            isAllowed('weekly-meeting') &&
            !isPBF
          }
        />
        <Route
          key="add-new-weekly-meeting-report"
          exact
          path={pathnames.addNewWeeklyMeetingReport()}
          render={() => <AddNewWeeklyMeetingReport />}
          enabled={
            FEATURE_FLAGS.routes.weeklyMeeting() &&
            isAllowed('weekly-meeting', 'create') &&
            !isPBF
          }
        />
        <Route
          key="join-visit"
          exact
          path={pathnames.joinVisit()}
          render={() => <JoinVisit />}
          enabled={
            (isAllowed('join-visit.this-month') ||
              isAllowed('join-visit.historical')) &&
            !isPBF
          }
        />
        <Route
          key="one-on-one"
          exact
          path={pathnames.oneOnOne()}
          render={() => <OneOnOne />}
          enabled={
            // SPV
            ((isAllowed('1-on-1.sales') ||
              isAllowed('1-on-1.maintain') ||
              isAllowed('1-on-1.recruit')) &&
              !isPBF) ||
            // BM
            ((isAllowed('1-on-1-bm.sales') ||
              isAllowed('1-on-1-bm.maintain') ||
              isAllowed('1-on-1-bm.recruit')) &&
              featureFlags.features.oneOnOneBmSpv())
          }
        />
        <Route
          key="add-new-one-on-one-report"
          exact
          path={pathnames.addNewOneOnOneReport()}
          render={() => <AddNewOneOnOneReport />}
          enabled={
            (isAllowed('1-on-1', 'create') ||
              isAllowed('1-on-1-bm', 'create')) &&
            !isPBF
          }
        />
        <Route
          key="add-new-report join-visit"
          exact
          path={pathnames.addNewJoinVisitReport()}
          render={() => <AddNewJoinVisitReport />}
          enabled={isAllowed('join-visit', 'create') && !isPBF}
        />

        <Route
          key="maintain-report"
          exact
          enabled={isAllowed('report.maintain')}
          path={pathnames.maintainReport()}
          render={() => <MaintainReport />}
        />

        <Route
          key="maintain-report-list"
          exact
          enabled={isAllowed('report.maintain.list')}
          path={`${pathnames.maintainReportList(':agent_marketing_id')}`}
          render={() => <MaintainReportList />}
        />

        <Route
          key="recruit-report"
          exact
          enabled={isAllowed('report.recruit') && !isPBF}
          path={pathnames.recruitReport()}
          render={() => <RecruitReport />}
        />

        <Route
          key="leads-tracker"
          exact
          enabled={isAllowed('leads-tracker') && !isPBF}
          path={pathnames.leadsTracker()}
          render={() => <LeadsTracker />}
        />
        <Route
          key="leads-tracker-detail"
          exact
          enabled={isAllowed('leads-tracker') && !isPBF}
          path={pathnames.leadsTrackerDetail(
            ':agentId',
            ':applicationGroupId',
            ':applicationId',
          )}
          render={() => <LeadsTrackerDetail />}
        />

        <Route
          key="user-guide"
          exact
          path={pathnames.userGuide()}
          render={() => <UserGuide />}
        />
        <Route
          key="UnauthorizedPage"
          path={pathnames.unauthorized()}
          component={UnauthorizedPage}
        />
        <Route key="NotFoundPage" path="*" component={NotFoundPage} />
      </Switch>
    </PrivateLayout>
  );
};
